import {
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { Dispatch, SetStateAction } from 'react';

// Реестр, чьи фильтры переживают уход на другой раздел бокового меню и
// возврат обратно (issue/11161).
export type RegistryFiltersKey =
  | 'tasks'
  | 'analytics'
  | 'finances'
  | 'employees'
  | 'sendouts'
  | 'warehouse'
  | 'medical-cards';

// Хранилище вкладочное — sessionStorage, как у реестра записей
// (`recordsFiltersStorage.ts`, решение Михаила 06.09.2026): фильтры живут,
// пока открыта вкладка, новая вкладка и новый сеанс начинают с умолчаний.
const storageKey = (registry: RegistryFiltersKey): string =>
  `${registry}:registry:filters`;

// Опции селектов часто несут подпись-элемент (например, `WidthArchiveLabel`).
// В JSON элемент превращается в объект `{ key, ref, props, _owner }`, и после
// восстановления React падает на его отрисовке (Minified React error #31).
// Поэтому элементы в хранилище не пишем: без `label` Select берёт подпись из
// своих `options` по `value`.
const omitReactElements = (_key: string, value: unknown): unknown =>
  isValidElement(value) ? undefined : value;

// Фильтры, сохранённые до исправления, уже содержат сериализованные элементы —
// выбрасываем их при чтении, иначе вкладка так и не откроет реестр.
const isSerializedReactElement = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  'props' in value &&
  'key' in value &&
  'ref' in value;

const reviveRegistryFilters = (_key: string, value: unknown): unknown =>
  isSerializedReactElement(value) ? undefined : value;

const readRegistryFilters = (
  registry: RegistryFiltersKey
): Record<string, unknown> => {
  try {
    const rawValue = sessionStorage.getItem(storageKey(registry));
    if (!rawValue) return {};

    const parsedValue: unknown = JSON.parse(rawValue, reviveRegistryFilters);

    return typeof parsedValue === 'object' && parsedValue !== null
      ? (parsedValue as Record<string, unknown>)
      : {};
  } catch {
    // Приватный режим и переполненное хранилище не должны ломать реестр.
    return {};
  }
};

const writeRegistryFilter = (
  registry: RegistryFiltersKey,
  field: string,
  value: unknown
) => {
  try {
    sessionStorage.setItem(
      storageKey(registry),
      JSON.stringify(
        { ...readRegistryFilters(registry), [field]: value },
        omitReactElements
      )
    );
  } catch {
    // См. readRegistryFilters.
  }
};

export const clearRegistryFilters = (registry: RegistryFiltersKey) => {
  try {
    sessionStorage.removeItem(storageKey(registry));
  } catch {
    // См. readRegistryFilters.
  }
};

// Замена useState для значения фильтра реестра: интерфейс тот же, включая
// функциональный апдейт, поэтому страница меняет только объявление состояния.
// Значение восстанавливается при монтировании и пишется в хранилище на каждое
// изменение.
export const useRegistryFilterState = <T>(
  registry: RegistryFiltersKey,
  field: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setStateValue] = useState<T>(() => {
    const storedValue = readRegistryFilters(registry)[field];

    return storedValue === undefined ? initialValue : (storedValue as T);
  });

  // Пишем в хранилище отдельным эффектом: так функциональный апдейт и
  // несколько подряд идущих изменений сохраняют последнее значение.
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    writeRegistryFilter(registry, field, value);
  }, [field, registry, value]);

  const setValue = useCallback<Dispatch<SetStateAction<T>>>((nextValue) => {
    setStateValue(nextValue);
  }, []);

  return [value, setValue];
};
